import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logger } from '@/core/logging/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * The one class component in the codebase.
 *
 * niraxobid.md section 3 rule 4 says "functional components only", but React
 * provides no functional error-boundary primitive and `react-error-boundary`
 * would be a new dependency (section 3 rule 6). This is a documented exception.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error('Unhandled render error', error, info.componentStack);
  }

  private readonly handleReset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (!error) return children;
    if (fallback) return fallback;

    return (
      <div className="app-error" role="alert">
        <h1 className="app-error__title">Something went wrong</h1>
        <p className="app-error__message">{error.message}</p>
        <button type="button" className="btn btn--soft" onClick={this.handleReset}>
          <span className="btn__label">Try again</span>
        </button>
      </div>
    );
  }
}
